import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CellProps } from 'react-datasheet-grid'
import type { DeviceSpecDevice, SpecMachineItem } from '../../types'
import { deviceLabel } from './masterView'

// 태그 고르기 칸 (인터페이스 항목의 주소) — 고른 디바이스의 태그 목록에서 고른다. 디바이스를 안 골랐으면 모든 디바이스의 태그에서
// 골라 디바이스·주소가 함께 채워진다. 목록 위 입력칸에 치면 주소·Alias·설명으로 거르고, 맞는 게 없으면 친 값을 주소로 쓴다.
// 여는 방법은 ChoiceCell 과 같다 — 칸을 고른 뒤 한 번 더 클릭·Enter·F2. ↑↓ 로 고르고 Enter 로 확정, Esc 는 취소.
// 붙여넣기·지우기·채우기 핸들은 주소 문자열 그대로 (그리드가 처리).

export interface TagPickData { devices: DeviceSpecDevice[] }

interface Option { device: string; address: string; deviceName: string; detail: string }
const LIMIT = 300

export default function TagPickCell({ rowData, setRowData, focus, active, stopEditing, columnData }: CellProps<SpecMachineItem, TagPickData>) {
  const { devices } = columnData
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [clicked, setClicked] = useState(false)
  const [box, setBox] = useState<{ left: number; top: number; width: number } | null>(null)
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  const open = focus || (clicked && active)

  // 고른 디바이스의 태그, 없으면 전체 (주소 있는 태그만)
  const all = useMemo<Option[]>(() => {
    const scope = rowData.device ? devices.filter(d => d.key === rowData.device) : devices
    return scope.flatMap(d => d.tags.filter(t => t.address.trim()).map(t => ({
      device: d.key, address: t.address.trim(), deviceName: deviceLabel(d),
      detail: [t.alias, t.description, t.dataType].filter(Boolean).join(' · '),
    })))
  }, [devices, rowData.device])
  const q = query.trim().toLowerCase()
  const options = useMemo(() => (q ? all.filter(o => [o.address, o.detail, o.deviceName].some(v => v.toLowerCase().includes(q))) : all).slice(0, LIMIT), [all, q])

  // 열릴 때 — 칸 아래에 띄우고 지금 값에 맞춘다
  useEffect(() => {
    if (!open || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    const raf = requestAnimationFrame(() => {
      setQuery('')
      const i = all.findIndex(o => o.device === rowData.device && o.address === rowData.address.trim())
      setIndex(Math.max(0, i))
      setBox({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 340) })
      inputRef.current?.focus()
    })
    return () => cancelAnimationFrame(raf)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active && clicked) {
      const raf = requestAnimationFrame(() => setClicked(false))
      return () => cancelAnimationFrame(raf)
    }
  }, [active, clicked])

  useEffect(() => {
    if (!clicked) return
    const onDown = () => setClicked(false)
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [clicked])

  const close = (nextRow = false) => {
    if (focus) stopEditing({ nextRow })
    setClicked(false)
  }
  const choose = (o: Option | undefined, nextRow = false) => {
    if (o) setRowData({ ...rowData, device: o.device, address: o.address })
    else if (query.trim()) setRowData({ ...rowData, address: query.trim() }) // 목록에 없는 주소를 직접
    close(nextRow)
  }

  const onMouseDown = (e: React.MouseEvent) => {
    if (!active || e.button !== 0 || e.shiftKey || e.ctrlKey || e.metaKey) return
    e.preventDefault()
    e.stopPropagation()
    if (open) close()
    else setClicked(true)
  }

  // 열려 있는 동안 이동·확정 키는 목록이 먼저 (글자는 입력칸으로)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return
      let handled = true
      if (e.key === 'ArrowDown') setIndex(i => Math.min(i + 1, options.length - 1))
      else if (e.key === 'ArrowUp') setIndex(i => Math.max(i - 1, 0))
      else if (e.key === 'Enter' || e.key === 'Tab') choose(options[index], e.key === 'Enter')
      else if (e.key === 'Escape') close()
      else handled = false
      if (handled) { e.preventDefault(); e.stopImmediatePropagation() }
      else e.stopImmediatePropagation() // 글자·지우기는 입력칸에만 — 그리드가 칸 내용을 지우지 않게
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })

  // 목록에서 고른 항목이 보이게
  const listRef = useRef<HTMLDivElement>(null)
  useEffect(() => { listRef.current?.querySelector(`[data-i="${index}"]`)?.scrollIntoView({ block: 'nearest' }) }, [index])

  return (
    <div ref={ref} onMouseDown={onMouseDown} className="w-full h-full flex items-center px-2 text-xs cursor-pointer select-none min-w-0 font-mono">
      {rowData.address
        ? <span className="truncate">{rowData.address}</span>
        : !active && <span className="text-gray-400 font-sans">{rowData.device ? '태그 고르기' : ''}</span>}
      {(active || open) && (
        <svg className="w-3 h-3 ml-auto shrink-0 text-gray-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {open && box && createPortal(
        <div className="choice-list fixed z-[60] rounded-lg border border-gray-200 bg-surface shadow-lg text-xs font-sans"
          style={{ left: box.left, top: box.top, width: box.width }}
          onMouseDown={e => { e.stopPropagation(); if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault() }}>
          <div className="p-1.5 border-b border-gray-100">
            <input ref={inputRef} value={query} onChange={e => { setQuery(e.target.value); setIndex(0) }}
              placeholder={rowData.device ? '주소, Alias, 설명으로 검색' : '전체 태그에서 검색'}
              className="w-full border border-gray-300 rounded px-2 py-1 text-xs outline-none focus:border-blue-400" />
          </div>
          <div ref={listRef} className="max-h-72 overflow-y-auto py-1">
            {options.length === 0 && (
              <div className="px-3 py-2 text-gray-400">
                {all.length === 0 ? (rowData.device ? '이 디바이스에 주소가 있는 태그가 없습니다.' : '디바이스 명세에 태그가 없습니다.')
                  : query.trim() ? `맞는 태그가 없습니다. Enter를 누르면 '${query.trim()}'을(를) 주소로 씁니다.` : ''}
              </div>
            )}
            {options.map((o, i) => (
              <div key={`${o.device}:${o.address}:${i}`} data-i={i} onMouseEnter={() => setIndex(i)} onClick={() => choose(o)}
                className={`px-3 py-1.5 cursor-pointer flex items-center gap-2 whitespace-nowrap ${i === index ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}>
                {!rowData.device && <span className="text-gray-400 truncate max-w-[110px]">{o.deviceName}</span>}
                <span className="font-mono">{o.address}</span>
                <span className="text-gray-400 truncate">{o.detail}</span>
                {o.device === rowData.device && o.address === rowData.address.trim() && <span className="ml-auto pl-2 text-gray-400">✓</span>}
              </div>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
